import { QuestionAnswer } from '../types/Question'

type Answer = string | string[] | undefined | null

// Single-select answers are stored as one answer id, multi-select (checkbox) answers as a list of ids.
export const normalizeAnswerIds = (answer: Answer): string[] => {
    if (Array.isArray(answer)) return answer.map(String)
    if (answer === undefined || answer === null) return []
    return [String(answer)]
}

// Order-insensitive exact match: a multi-select answer is correct only when it selects exactly the
// trainer's set. Mirrors answers_match in LITTWebBackendLambda's controller.py.
export const answersMatch = (learnerAnswer: Answer, trainerAnswer: Answer): boolean => {
    const learnerIds = normalizeAnswerIds(learnerAnswer).sort()
    const trainerIds = normalizeAnswerIds(trainerAnswer).sort()
    return learnerIds.length === trainerIds.length && learnerIds.every((id, i) => id === trainerIds[i])
}

// Labels of the selected answers in template order, or '-' when nothing matches.
export const answerToLabel = (answer: Answer, questionAnswers: QuestionAnswer[] = []): string => {
    const ids = normalizeAnswerIds(answer)
    const labels = questionAnswers
        .filter((a) => ids.includes(String(a.answerId)))
        .map((a) => a.answerLabel)
    return labels.length > 0 ? labels.join(', ') : '-'
}

// Partial credit (0 to 1) per the D480350740 scoring policy, mirroring answer_credit in
// LITTWebBackendLambda's controller.py: max(0, correct picks - incorrect picks) / trainer's correct options.
// Deducting incorrect picks closes the "select every option" loophole; single-select is 1 or 0.
export const answerCredit = (learnerAnswer: Answer, trainerAnswer: Answer): number => {
    if (trainerAnswer === undefined || trainerAnswer === null) return 0
    const trainerIds = new Set(normalizeAnswerIds(trainerAnswer))
    const learnerIds = new Set(normalizeAnswerIds(learnerAnswer))
    if (trainerIds.size === 0) return learnerIds.size === 0 ? 1 : 0
    let correct = 0
    learnerIds.forEach((id) => { if (trainerIds.has(id)) correct++ })
    const incorrect = learnerIds.size - correct
    return Math.max(0, correct - incorrect) / trainerIds.size
}
